import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TemplateValidationResult, VariableConversion } from '../../../services/template-validation.service';
import { TemplateModel } from '../../../models/email-sequence.model';

@Component({
  selector: 'app-template-conversion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ChipModule,
    DividerModule,
    ScrollPanelModule
  ],
  template: `
    <div class="template-conversion-dialog">
      <div class="dialog-header mb-4">
        <h2 class="text-xl font-semibold mb-2">Template Variable Incompatibility</h2>
        <p class="text-gray-600">
          The selected template "{{templateName}}" contains variables that are not compatible with {{platformName}}.
        </p>
      </div>

      <div class="conversion-details mb-4" *ngIf="validationResult">
        <p-card header="Variable Conversions Needed" styleClass="mb-3">
          <div class="conversions-list">
            <div *ngFor="let conversion of validationResult.conversions" 
                 class="conversion-item flex align-items-center justify-content-between mb-2 p-2 surface-50 border-round">
              <div class="flex align-items-center">
                <p-chip label="{{'{{'}}{{conversion.original}}{{'}}'}}" 
                        [style]="{'background-color': '#fee2e2', 'color': '#dc2626'}" class="mr-2"></p-chip>
                <i class="pi pi-arrow-right mx-2 text-gray-500"></i>
                <p-chip label="{{'{{'}}{{conversion.converted}}{{'}}'}}" 
                        [style]="{'background-color': '#dcfce7', 'color': '#16a34a'}"></p-chip>
              </div>
            </div>
          </div>
        </p-card>

        <p-card header="Preview of Converted Template" *ngIf="validationResult.convertedSubject || validationResult.convertedBody">
          <div class="converted-preview">
            <div class="mb-3" *ngIf="validationResult.convertedSubject">
              <strong>Subject:</strong>
              <div class="mt-1 p-2 surface-100 border-round">
                {{validationResult.convertedSubject}}
              </div>
            </div>
            <div *ngIf="validationResult.convertedBody">
              <strong>Body:</strong>
              <div class="mt-1 p-2 surface-100 border-round" style="max-height: 200px; overflow-y: auto;">
                <pre class="whitespace-pre-wrap text-sm">{{validationResult.convertedBody}}</pre>
              </div>
            </div>
          </div>
        </p-card>
      </div>

      <div class="invalid-variables mb-4" *ngIf="validationResult && validationResult.invalidVariables && validationResult.invalidVariables.length > 0">
        <p-card header="Unrecognized Variables" 
                subheader="These variables cannot be automatically converted and may need manual review" 
                styleClass="mb-3">
          <div class="invalid-list">
            <p-chip *ngFor="let variable of validationResult.invalidVariables" 
                    label="{{'{{'}}{{variable}}{{'}}'}}" 
                    [style]="{'background-color': '#fef3c7', 'color': '#d97706'}" 
                    class="mr-1 mb-1"></p-chip>
          </div>
        </p-card>
      </div>

      <div class="dialog-actions flex justify-content-end gap-2">
        <button pButton 
                type="button" 
                label="Cancel" 
                class="p-button-text" 
                (click)="onCancel()">
        </button>
        <button pButton 
                type="button" 
                label="Create Converted Template" 
                class="p-button-primary" 
                (click)="onCreateTemplate()"
                [disabled]="!canCreateTemplate()">
          <i class="pi pi-plus mr-1"></i>
          Create {{newTemplateName}}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .template-conversion-dialog {
      min-width: 600px;
      max-width: 800px;
    }

    .conversion-item {
      border: 1px solid #e5e7eb;
    }

    .converted-preview pre {
      font-family: inherit;
      margin: 0;
    }

    .invalid-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
    }

    .dialog-actions {
      border-top: 1px solid #e5e7eb;
      padding-top: 1rem;
      margin-top: 1rem;
    }

    :host ::ng-deep .p-card .p-card-header {
      font-weight: 600;
      font-size: 1rem;
    }

    :host ::ng-deep .p-card .p-card-body {
      padding-top: 0.5rem;
    }
  `]
})
export class TemplateConversionDialogComponent implements OnInit {
  templateName: string = '';
  platformName: string = '';
  targetPlatform: 'instantly' | 'smartlead' | 'hubspot' = 'instantly';
  validationResult: TemplateValidationResult | null = null;
  originalTemplate: TemplateModel | null = null;
  newTemplateName: string = '';

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit(): void {
    const data = this.config.data;
    this.templateName = data.templateName || 'Unknown Template';
    this.targetPlatform = data.targetPlatform || 'instantly';
    this.platformName = this.targetPlatform === 'instantly' ? 'Instantly' : 
                        this.targetPlatform === 'smartlead' ? 'Smartlead' : 'HubSpot';
    this.validationResult = data.validationResult;
    this.originalTemplate = data.originalTemplate;
    this.newTemplateName = data.newTemplateName || `${this.platformName} - ${this.templateName}`;
  }

  canCreateTemplate(): boolean {
    return !!(this.validationResult && 
             this.validationResult.conversions.length > 0 && 
             this.validationResult.convertedSubject && 
             this.validationResult.convertedBody);
  }

  onCancel(): void {
    this.ref.close({ action: 'cancel' });
  }

  onCreateTemplate(): void {
    if (!this.canCreateTemplate() || !this.validationResult || !this.originalTemplate) {
      return;
    }

    const convertedTemplate: Partial<TemplateModel> = {
      name: this.newTemplateName,
      subject: this.validationResult.convertedSubject!,
      body: this.validationResult.convertedBody!,
      type: this.originalTemplate.type || 'email'
    };

    this.ref.close({ 
      action: 'create', 
      template: convertedTemplate,
      conversions: this.validationResult.conversions 
    });
  }
}